import { useEffect, useState } from 'react'

export interface AppConfig {
  platformFeeGbp: number
  searchRadiusKm: number
  maxPlumbersToCall: number
  stripePublishableKey: string | null
  paywallEnabled: boolean
}

const DEFAULT_CONFIG: AppConfig = {
  platformFeeGbp: 34,
  searchRadiusKm: 8,
  maxPlumbersToCall: 10,
  stripePublishableKey: null,
  paywallEnabled: true,
}

// Mirrors the server's own config cache TTL (configService.ts) and the
// Cache-Control header on /api/config/public, so a toggle flipped in Admin
// Settings reaches every open tab within the same bounded window instead of
// being cached for the tab's entire lifetime. A shared interval (not a
// mount-time check) is required for this to actually hold for a component
// that stays mounted the whole time — e.g. a customer sitting on the job
// status page waiting for a plumber never remounts anything.
const CACHE_TTL_MS = 60_000

let cached: AppConfig | null = null
const subscribers = new Set<(config: AppConfig) => void>()
let refreshTimer: ReturnType<typeof setInterval> | null = null

function refresh(): void {
  fetch('/api/config/public')
    .then(r => r.json())
    .then((data: AppConfig) => {
      cached = data
      subscribers.forEach(notify => notify(data))
    })
    .catch(() => {})
}

export function useAppConfig(): AppConfig {
  const [config, setConfig] = useState<AppConfig>(cached ?? DEFAULT_CONFIG)

  useEffect(() => {
    subscribers.add(setConfig)
    if (cached) setConfig(cached)
    else refresh()
    if (!refreshTimer) refreshTimer = setInterval(refresh, CACHE_TTL_MS)

    return () => {
      subscribers.delete(setConfig)
      if (subscribers.size === 0 && refreshTimer) {
        clearInterval(refreshTimer)
        refreshTimer = null
      }
    }
  }, [])

  return config
}
